import { useEffect, useState } from 'react';

import { api, getUser } from '../api.js';
import Modal from './Modal.jsx';
import ScopeSelect from './ScopeSelect.jsx';

const STATUSES = ['offen', 'in Arbeit', 'erledigt'];

/**
 * Modal zum Anlegen eines Kurseinstellungs-Eintrags.
 *
 * Props:
 *  - mandanten: Mandantenliste fürs Geltungs-Dropdown
 *  - courseSets: Kursset-Liste fürs Geltungs-Dropdown
 *  - defaultScope?: { mandant_id, course_set } als Vorbelegung
 *  - onClose: () => void
 *  - onSaved: () => Promise|void
 *
 * Autor: Dropdown der bestehenden User, vorausgewählt ist der eingeloggte
 * User – analog zum Ereignis-Formular.
 */
export default function CourseSettingEventFormModal({
    mandanten,
    courseSets,
    defaultScope,
    onClose,
    onSaved,
}) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [scope, setScope] = useState(
        defaultScope || { mandant_id: null, course_set: null }
    );
    const [status, setStatus] = useState('offen');
    const [author, setAuthor] = useState(getUser() || '');
    const [authorOptions, setAuthorOptions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        api.listUserOptions()
            .then((users) => {
                if (cancelled) return;
                setAuthorOptions(users);
                // Falls der eingeloggte User nicht in der Liste auftaucht,
                // wenigstens als freien Wert mit anbieten.
                const me = getUser();
                if (me && !users.some((u) => u.username === me)) {
                    setAuthorOptions([{ id: -1, username: me }, ...users]);
                }
            })
            .catch(() => { /* unkritisch – Autor bleibt leer wählbar */ });
        return () => { cancelled = true; };
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            await api.createCourseSettingEvent({
                title,
                description: description || null,
                mandant_id: scope.mandant_id,
                course_set: scope.course_set,
                status,
                author: author || null,
            });
            await onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            title="Neuer Eintrag: Kurseinstellungen"
            onClose={onClose}
            footer={
                <>
                    <button type="button" className="btn" onClick={onClose}>
                        Abbrechen
                    </button>
                    <button
                        type="submit"
                        form="course-setting-form"
                        className="btn btn--primary"
                        disabled={saving}
                    >
                        {saving ? 'Speichere…' : 'Speichern'}
                    </button>
                </>
            }
        >
            <form id="course-setting-form" onSubmit={handleSubmit}>
                {error && <div className="login__error">{error}</div>}

                <div className="field">
                    <label>Titel *</label>
                    <input
                        type="text"
                        required
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="z. B. Neues Pflichtmodul aktiviert"
                    />
                </div>

                <div className="grid-2">
                    <div className="field">
                        <label>Geltung</label>
                        <ScopeSelect
                            value={scope}
                            onChange={setScope}
                            mandanten={mandanten}
                            courseSets={courseSets}
                        />
                    </div>
                    <div className="field">
                        <label>Status</label>
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label>Autor</label>
                    <select value={author} onChange={(e) => setAuthor(e.target.value)}>
                        <option value="">– kein Autor –</option>
                        {authorOptions.map((u) => (
                            <option key={u.id} value={u.username}>{u.username}</option>
                        ))}
                    </select>
                </div>

                <div className="field">
                    <label>Beschreibung</label>
                    <textarea
                        rows={5}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>

                <div className="muted" style={{ fontSize: 12 }}>
                    Auf der Detailseite lässt sich die Beschreibung als Markdown
                    ausformulieren.
                </div>
            </form>
        </Modal>
    );
}
